import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Car, CornerDownLeft, Search, SearchX, Users } from 'lucide-react';
import { PARTNER_NAV_LINKS, filterPartnerNavigation } from '../navigation';
import { searchPartnerData } from '../services/search';
import { Empty, ErrorBox, Skeleton } from '../ui/kit';

// ── Globale Command-Palette (⌘K) — von jedem Portal-Screen aus. ──
// Inhalt: (a) Bereiche (bestehende Nav-Filterlogik), (b) ab 2 Zeichen debounced Datentreffer
// von GET /partners/search (Fahrer/Fahrzeuge, aufs eigene Konto gescopt), (c) Aktionszeile
// „Im Auftragsbuch suchen". Vor der Freigabe (dataEnabled=false) nur Bereiche, keine Datensuche.

const GROUP_ICONS = { drivers: Users, vehicles: Car };

// Stöbern-Einträge (Owner-Entscheid 26.08.: alles sehen ohne Tipparbeit): reine Navigation
// auf bestehende Screens, deshalb unabhängig von der Datensuche immer verfügbar.
const BROWSE_LINKS = [
  { to: '/drivers', label: 'Ganze Flotte anzeigen', icon: Users },
  { to: '/vehicles', label: 'Alle Fahrzeuge', icon: Car },
  { to: '/order-log', label: 'Auftragsbuch öffnen', icon: BookOpen },
];

// Deep-Link je Treffer-Typ: Fahrerprofil hat eine eigene Route, Fahrzeuge die Bestandsseite.
const dataTarget = (groupKey, item) => {
  const type = String(item.type || groupKey || '');
  if (/^drivers?$/.test(type)) return `/drivers/${item.id}`;
  if (/^vehicles?$/.test(type)) return '/vehicles';
  return null;
};

export default function CommandPalette({ open, onClose, dataEnabled = true }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const seqRef = useRef(0);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const [data, setData] = useState({ status: 'idle', groups: [] });
  const trimmed = query.trim();

  // Öffnen: Zustand zurücksetzen, Fokus ins Eingabefeld; Schließen: Fokus kehrt zurück.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setActive(0);
    setData({ status: 'idle', groups: [] });
    const previouslyFocused = document.activeElement;
    window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      seqRef.current += 1;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [open]);

  // Datensuche: ab 2 Zeichen, ~300 ms debounced; Antworten außer der Reihe werden verworfen.
  useEffect(() => {
    if (!open || !dataEnabled) return undefined;
    if (trimmed.length < 2) {
      setData({ status: 'idle', groups: [] });
      return undefined;
    }
    const seq = ++seqRef.current;
    setData((d) => ({ status: 'loading', groups: d.groups }));
    const timer = window.setTimeout(() => {
      searchPartnerData(trimmed)
        .then((result) => { if (seq === seqRef.current) setData({ status: 'ready', groups: result.groups }); })
        .catch(() => { if (seq === seqRef.current) setData({ status: 'error', groups: [] }); });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, dataEnabled, trimmed, retryTick]);

  // Bereiche: dieselbe Filterlogik wie die Sidebar-Suche (Label + Beschreibung).
  const navRows = useMemo(() => filterPartnerNavigation(PARTNER_NAV_LINKS, query), [query]);

  // Flache Zeilenliste (Pfeiltasten über ALLE Zeilen) mit Abschnitts-Beschriftung.
  const rows = useMemo(() => {
    const out = [];
    // Leerzustand (< 2 Zeichen): Stöbern-Gruppe zuerst — Enter auf Zeile 1 zeigt sofort alles.
    if (trimmed.length < 2) {
      BROWSE_LINKS.forEach((link) => out.push({
        kind: 'browse', section: 'Stöbern', key: `browse:${link.to}`,
        icon: link.icon, title: link.label, to: link.to,
      }));
    }
    navRows.forEach((link) => out.push({
      kind: 'nav', section: 'Bereiche', key: `nav:${link.to}`,
      icon: link.icon, title: link.label, subtitle: link.description, to: link.to,
    }));
    if (dataEnabled && data.status !== 'error') {
      data.groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          const to = dataTarget(group.key, item);
          if (!to) return;
          out.push({
            kind: 'data', section: group.label || group.key, key: `data:${group.key}:${item.id}`,
            icon: GROUP_ICONS[group.key] || Search, title: item.title,
            subtitle: item.subtitle, status: item.status, to,
          });
        });
      });
    }
    if (dataEnabled && trimmed) {
      out.push({
        kind: 'action', section: 'Aktionen', key: 'action:order-log',
        icon: CornerDownLeft, title: `Im Auftragsbuch suchen: „${trimmed}“`,
        to: `/order-log?search=${encodeURIComponent(trimmed)}`,
      });
    }
    return out;
  }, [navRows, dataEnabled, data.status, data.groups, trimmed]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => { setActive((i) => Math.max(0, Math.min(i, Math.max(rows.length - 1, 0)))); }, [rows.length]);
  useEffect(() => {
    const el = document.getElementById(`vg-cmdk-row-${active}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const activate = useCallback((row) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  }, [navigate, onClose]);

  // Fokus-Falle: Tab bleibt im Modal (Eingabefeld), Esc schließt, Pfeile + Enter steuern die Liste.
  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key === 'Tab') { event.preventDefault(); inputRef.current?.focus(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!rows.length) return;
      setActive((i) => (event.key === 'ArrowDown' ? (i + 1) % rows.length : (i - 1 + rows.length) % rows.length));
      return;
    }
    if (event.key === 'Enter') { event.preventDefault(); activate(rows[active]); }
  };

  if (!open) return null;

  const showEmpty = rows.filter((r) => r.kind !== 'action').length === 0 && data.status !== 'loading' && data.status !== 'error';
  let lastSection = null;

  return (
    <div className="vg-cmdk-overlay" onMouseDown={onClose} onKeyDown={onKeyDown}>
      <div className="vg-cmdk" role="dialog" aria-modal="true" aria-label="Globale Suche" onMouseDown={(e) => e.stopPropagation()}>
        <div className="vg-cmdk-head">
          <Search size={17} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={dataEnabled ? 'Bereiche, Fahrer, Fahrzeuge …' : 'Bereiche durchsuchen …'}
            aria-label="Global suchen"
            role="combobox"
            aria-expanded="true"
            aria-controls="vg-cmdk-list"
            aria-activedescendant={rows.length ? `vg-cmdk-row-${active}` : undefined}
          />
          <kbd>esc</kbd>
        </div>
        <div className="vg-cmdk-list" id="vg-cmdk-list" role="listbox" aria-label="Suchergebnisse">
          {rows.map((row, index) => {
            const header = row.section !== lastSection ? row.section : null;
            lastSection = row.section;
            return (
              <React.Fragment key={row.key}>
                {header && <div className="vg-cmdk-section">{header}</div>}
                <button
                  type="button"
                  id={`vg-cmdk-row-${index}`}
                  role="option"
                  aria-selected={index === active}
                  tabIndex={-1}
                  className={`vg-cmdk-row${index === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => activate(row)}
                >
                  {row.icon && <row.icon size={16} strokeWidth={1.8} aria-hidden="true" />}
                  <span className="vg-cmdk-row-main">
                    <span className="vg-cmdk-row-title">{row.title}</span>
                    {row.subtitle && <span className="vg-cmdk-row-sub">{row.subtitle}</span>}
                  </span>
                  {row.status && <span className="vg-cmdk-row-status">{row.status}</span>}
                </button>
              </React.Fragment>
            );
          })}
          {data.status === 'loading' && (
            <div className="vg-cmdk-loading" aria-label="Datensuche läuft">
              <Skeleton h={12} w="62%" /><Skeleton h={12} w="48%" /><Skeleton h={12} w="55%" />
            </div>
          )}
          {data.status === 'error' && (
            <div style={{ padding: '8px 16px' }}>
              <ErrorBox text="Datensuche derzeit nicht erreichbar." onRetry={() => setRetryTick((t) => t + 1)} />
            </div>
          )}
          {showEmpty && (
            <Empty
              icon={SearchX}
              title="Keine Treffer"
              hint={trimmed
                ? (dataEnabled ? 'Anderen Begriff versuchen — oder unten im Auftragsbuch suchen.' : 'Anderen Begriff versuchen.')
                : 'Tippen, um die Bereiche zu durchsuchen.'}
              style={{ padding: '26px 20px' }}
            />
          )}
        </div>
        <div className="vg-cmdk-foot">
          <span><kbd>↑</kbd> <kbd>↓</kbd> Navigieren</span>
          <span><kbd>↵</kbd> Öffnen</span>
          <span><kbd>esc</kbd> Schließen</span>
          {!dataEnabled && <span className="vg-cmdk-foot-note">Datensuche nach der Freischaltung verfügbar</span>}
          {dataEnabled && trimmed.length === 1 && <span className="vg-cmdk-foot-note">Datensuche ab 2 Zeichen</span>}
        </div>
      </div>
    </div>
  );
}
